import { colors, white } from '@kmns-internal/furikake'
import { AddOutlined, DeleteOutlined, MoreHoriz } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { IndicatedIssueLabel } from '@ulysses-inc/harami_api_client'
import type { KeyboardEvent, MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import Loading from 'src/components/loading/Loading'
import EditIndicatedIssueLabelDrawer from 'src/features/improves/labels/EditIndicatedIssueLabelDrawer'
import indicatedIssuesOperations from 'src/state/ducks/indicatedIssues/operations'
import type { RootState } from 'src/state/store'

interface StateProps {
  indicatedIssueLabels: Array<IndicatedIssueLabel>
  isLoading: boolean
}

interface DispatchProps {
  getIndicatedIssueLabels: () => void
  openAddIndicatedIssueLabel: () => void
  openEditIndicatedIssueLabel: (
    indicatedIssueLabel: IndicatedIssueLabel,
  ) => void
  deleteIndicatedIssueLabel: (uuid: string) => void
}

type IndicatedIssueLabelsProps = StateProps & DispatchProps

const useProps = (): IndicatedIssueLabelsProps => {
  const dispatch = useDispatch()
  const state = useSelector((state: RootState) => state)

  return {
    // -----------------------------------
    // StateProps
    // -----------------------------------

    indicatedIssueLabels:
      state.indicatedIssuesState.indicatedIssueLabel.indicatedIssueLabels,
    isLoading: state.indicatedIssuesState.indicatedIssueLabel.isLoading,

    // -----------------------------------
    // DispatchProps
    // -----------------------------------

    getIndicatedIssueLabels: () => {
      indicatedIssuesOperations.getIndicatedIssueLabels(dispatch)
    },
    openAddIndicatedIssueLabel: () => {
      indicatedIssuesOperations.resetEditIndicatedIssueLabel(dispatch)
      indicatedIssuesOperations.changeIsShowDrawer(dispatch, true)
    },
    openEditIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => {
      indicatedIssuesOperations.changeEditIndicatedIssueLabel(
        dispatch,
        indicatedIssueLabel,
      )
      indicatedIssuesOperations.changeIsShowDrawer(dispatch, true)
    },
    deleteIndicatedIssueLabel: (uuid: string) => {
      indicatedIssuesOperations.deleteIndicatedIssueLabel(dispatch, uuid)
    },
  }
}

const EditIndicatedIssueLabels = () => {
  // react-reduxのconnect関数の利用をやめた際のなごり
  // (今後このコンポーネントを触ることがあればそのときに解体する)
  const exProps = useProps()

  useEffect(() => {
    exProps.getIndicatedIssueLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const history = useHistory()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [labelToDelete, setLabelToDelete] =
    useState<IndicatedIssueLabel | null>(null)

  const handleMenuClick = (
    event: MouseEvent<HTMLElement>,
    label: IndicatedIssueLabel,
  ) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setLabelToDelete(label)
  }

  const handleMenuClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const handleDeleteConfirm = () => {
    if (labelToDelete?.uuid) {
      exProps.deleteIndicatedIssueLabel(labelToDelete.uuid)
    }
    setDeleteDialogOpen(false)
    setLabelToDelete(null)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setLabelToDelete(null)
  }

  const handleRowClick = (label: IndicatedIssueLabel) => {
    exProps.openEditIndicatedIssueLabel(label)
  }

  return (
    <Stack>
      <Header>
        <HeaderBackButtonMui onClick={() => history.goBack()} />
        <HeaderTitle title="ラベル" />
      </Header>
      <Stack>
        {exProps.isLoading ? (
          <Loading />
        ) : (
          <>
            <Stack direction="row" justifyContent="flex-end" sx={{ p: 2 }}>
              <Button
                variant="contained"
                onClick={() => exProps.openAddIndicatedIssueLabel()}
                startIcon={<AddOutlined />}
              >
                ラベルを追加
              </Button>
            </Stack>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table sx={{ bgcolor: white, width: '100%' }} stickyHeader>
                <TableHead sx={{ backgroundColor: white }}>
                  <TableRow>
                    <TableCell
                      size="small"
                      sx={{
                        width: '30%',
                      }}
                    >
                      ラベル名
                    </TableCell>
                    <TableCell
                      size="small"
                      sx={{
                        width: '30%',
                      }}
                    >
                      色
                    </TableCell>
                    <TableCell
                      size="small"
                      sx={{
                        width: '30%',
                      }}
                    >
                      プレビュー
                    </TableCell>
                    <TableCell
                      size="small"
                      sx={{
                        whiteSpace: 'normal',
                        backgroundColor: white,
                      }}
                      aria-label="操作"
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exProps.indicatedIssueLabels.map(
                    (indicatedIssueLabel: IndicatedIssueLabel) => (
                      <TableRow
                        key={indicatedIssueLabel.uuid}
                        onClick={() => handleRowClick(indicatedIssueLabel)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(
                          event: KeyboardEvent<HTMLTableRowElement>,
                        ) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            handleRowClick(indicatedIssueLabel)
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: colors.action.hover },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2">
                            {indicatedIssueLabel.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              backgroundColor: indicatedIssueLabel.color,
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={indicatedIssueLabel.name}
                            size="small"
                            sx={{
                              backgroundColor: indicatedIssueLabel.color,
                              color: colors.primary.contrastText,
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ minWidth: '96px' }}
                          align="right"
                          aria-label="操作"
                        >
                          <IconButton
                            onClick={e =>
                              handleMenuClick(e, indicatedIssueLabel)
                            }
                            aria-label="操作メニューを開く"
                          >
                            <MoreHoriz />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Stack>
      <EditIndicatedIssueLabelDrawer />

      {/* メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={event => {
            setDeleteDialogOpen(true)
            handleMenuClose(event)
          }}
          sx={{
            color: colors.error.main,
            '&:hover': {
              backgroundColor: colors.error.hover,
              color: colors.error.main,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>
          「{labelToDelete?.name}」の削除を実行しますがよろしいですか?
        </DialogTitle>
        <DialogActions>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleDeleteCancel}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default EditIndicatedIssueLabels
